import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Star, Target, Heart, Award, Shield, Users, TrendingUp, CheckCircle2, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [featuredReviews, setFeaturedReviews] = useState([]);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
      setFeaturedReviews(response.data.filter(r => r.featured).slice(0, 3));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => navigate('/')} className="flex items-center" data-testid="logo-home-button">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
                alt="ONYX Home Solutions" 
                className="h-12 lg:h-16 w-auto object-contain"
                style={{ background: 'transparent' }}
              />
            </button>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/products')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Products
              </button>
              <button onClick={() => navigate('/bulk-pricing')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Bulk
              </button>
              <button onClick={() => navigate('/guide')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Guides
              </button>
              <button onClick={() => navigate('/become-partner')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Become a Partner
              </button>
              <button onClick={() => navigate('/admin/login')} className="text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors" data-testid="admin-login-nav-button">
                Login
              </button>
              <button onClick={() => navigate('/cart')} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" style={{ color: '#8B6F47' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
            </div>
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => {
                const menu = document.getElementById('mobile-menu');
                menu.classList.toggle('hidden');
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        <div id="mobile-menu" className="hidden md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            <button onClick={() => navigate('/')} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Home
            </button>
            <button onClick={() => navigate('/products')} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Products
            </button>
            <button onClick={() => navigate('/bulk-pricing')} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Bulk
            </button>
            <button onClick={() => navigate('/guide')} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Guides
            </button>
            <button onClick={() => navigate('/become-partner')} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Become a Partner
            </button>
            <button onClick={() => navigate('/admin/login')} className="block w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-gray-50 rounded-lg">
              Login
            </button>
            <button onClick={() => navigate('/cart')} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Cart
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 lg:pt-20 min-h-screen flex items-end">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_711851d8-a783-4abf-8185-8548b17cf2e3/artifacts/11ep0zd4_image.png" 
            alt="Beautiful Modern Home with Pool and LED Lighting" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20 w-full">
          <div className="max-w-2xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight">
              Where Elegance<br />Meets Innovation.
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed">
              ONYX delivers opulent solutions that transform spaces with sophistication. Premium products crafted for those who demand excellence in every detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/products')} 
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 rounded-full text-lg font-medium"
              >
                Explore Products
              </Button>
              <Button 
                onClick={() => navigate('/become-partner')} 
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white/20 px-8 py-6 rounded-full text-lg font-medium"
              >
                Become a Partner
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
              Our Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              At ONYX Home Solutions, our values guide everything we do—from product design to customer service.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                icon: <Shield className="w-12 h-12" />,
                title: 'Integrity',
                desc: 'We stand behind every product with certifications, warranties, and transparent business practices. Your trust is our foundation.'
              },
              {
                icon: <Award className="w-12 h-12" />,
                title: 'Excellence',
                desc: 'Premium quality isn\'t optional—it\'s our standard. Every ONYX product undergoes rigorous testing to exceed industry benchmarks.'
              },
              {
                icon: <Heart className="w-12 h-12" />,
                title: 'Customer First',
                desc: 'Your satisfaction drives our innovation. We listen, adapt, and deliver solutions that make your life better.'
              }
            ].map((value, idx) => (
              <div key={idx} className="text-center p-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-50 mb-6 text-amber-600">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Target className="w-4 h-4" />
                OUR MISSION
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#1d1d1f' }}>
                Empowering Homes with Smart, Sustainable Solutions
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our mission is to transform how people experience their living spaces through innovative lighting and climate control solutions. We believe every home deserves access to technology that's energy-efficient, beautifully designed, and built to last.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                By partnering with contractors, electricians, and homeowners, we're creating brighter, more comfortable environments while reducing energy consumption and environmental impact.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1589530006797-d67347f18caa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxMRUQlMjBsaWdodGluZ3xlbnwwfHx8fDE3NjQ0NTM3MjR8MA&ixlib=rb-4.1.0&q=85" 
                alt="Modern Kitchen with Professional LED Lighting Installation" 
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <TrendingUp className="w-4 h-4" />
              OUR IMPACT
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
              Making a Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Real numbers. Real impact. See how ONYX is transforming homes and communities.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '15,000+', label: 'Homes Illuminated', desc: '4" ONYX Potlights installed nationwide' },
              { number: '85%', label: 'Energy Savings', desc: 'Average reduction in lighting costs' },
              { number: '5 Year', label: 'Warranty', desc: 'Industry-leading coverage' },
              { number: '98%', label: 'Customer Satisfaction', desc: 'Rated by verified customers' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-6 bg-gray-50 rounded-2xl">
                <p className="text-4xl lg:text-5xl font-bold text-amber-600 mb-2">{stat.number}</p>
                <p className="text-lg font-semibold mb-2" style={{ color: '#1d1d1f' }}>{stat.label}</p>
                <p className="text-sm text-gray-600">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1720463876942-ea209a85621a?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Craftsmanship and Quality" 
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Award className="w-4 h-4" />
                QUALITY COMMITMENT
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#1d1d1f' }}>
                Certified Excellence in Every Product
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Quality isn't just a promise—it's proven. Every ONYX product carries multiple safety certifications including ETL, UL, and CSA approval. Our 4" ONYX Potlights are IC-rated for direct insulation contact and wet location approved.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { label: 'Rigorous Testing', desc: 'Each product undergoes 200+ hours of quality testing' },
                  { label: '5-Year Warranty', desc: 'Industry-leading coverage on all lighting products' },
                  { label: 'Professional Grade', desc: 'Trusted by contractors and electricians nationwide' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900">{item.label}</p>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => navigate('/products')} 
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-full"
              >
                Explore Our Products
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Users className="w-4 h-4" />
              CUSTOMER REVIEWS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
              Trusted by Professionals
            </h2>
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-current text-yellow-400" />
              ))}
            </div>
            <p className="text-lg text-gray-600">Rated 5.0 out of 5 by our customers</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {featuredReviews.map((review) => (
              <div key={review.id} className="bg-gray-50 p-8 rounded-3xl">
                <div className="flex mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 text-base leading-relaxed">"{review.comment}"</p>
                <div>
                  <p className="font-semibold text-sm" style={{ color: '#1d1d1f' }}>{review.name}</p>
                  <p className="text-xs text-gray-500">Verified Customer</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer - Logo removed, text aligned */}
      <footer className="py-12 px-4 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-gray-400 text-sm">Premium LED lighting and HVAC solutions for modern homes.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/products')} className="text-gray-400 hover:text-white">ONYX Lighting</button></li>
                <li><button onClick={() => navigate('/products')} className="text-gray-400 hover:text-white">ONYX HVAC</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/')} className="text-gray-400 hover:text-white">About Us</button></li>
                <li><button onClick={() => navigate('/become-partner')} className="text-gray-400 hover:text-white">Become a Partner</button></li>
                <li><button onClick={() => navigate('/guide')} className="text-gray-400 hover:text-white">Guides</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@onyx-homesolutions.com" className="text-gray-400 hover:text-white flex items-center gap-2">
                  <Mail className="w-4 h-4" /> info@onyx-homesolutions.com
                </a></li>
                <li><a href="tel:519-996-2866" className="text-gray-400 hover:text-white flex items-center gap-2">
                  <Phone className="w-4 h-4" /> 519-996-2866
                </a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>© 2025 ONYX Home Solutions. All rights reserved. | Powered by Syntrexe</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;